import React from "react";
import { LineChart as ChartIcon } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CareerCard } from "./CareerCard";
import { translateFr as tfr } from "@/i18n";

const MONTHS_FR = ["Jan.", "Fév.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."].map((s) => tfr(s));

function monthLabel(m) {
  const idx = Number((m || "").slice(5, 7)) - 1;
  return MONTHS_FR[idx] || m;
}

export function MonthlyProgressChart({ history }) {
  const data = (history || [])
    .filter((h) => h.velocity_score != null || h.readiness_score != null)
    .map((h) => ({ month: monthLabel(h.month), velocity: h.velocity_score, readiness: h.readiness_score }));

  return (
    <CareerCard icon={ChartIcon} title={tfr("Progression mensuelle")}
      subtitle={tfr("Évolution réelle des scores Career Velocity et Readiness (un relevé par mois)")}
      testid="monthly-progress-card">
      {data.length < 2 ? (
        <div className="text-center py-10" data-testid="progress-empty">
          <p className="text-sm text-muted-foreground">{tfr("Les données de progression apparaîtront ici au fil du temps.")}</p>
          <p className="text-[11px] text-muted-foreground/70 mt-1">
            {tfr("Un relevé est enregistré automatiquement chaque mois — aucune donnée n'est simulée.")}
          </p>
        </div>
      ) : (
        <div className="h-56" data-testid="progress-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="velGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E11D2E" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#E11D2E" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={34} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }}
                formatter={(v, name) => [v, name === "velocity" ? "Career Velocity" : "Career Readiness"]}
              />
              <Area type="monotone" dataKey="velocity" name="velocity" stroke="#E11D2E" strokeWidth={2}
                fill="url(#velGrad)" dot={{ r: 3, fill: "#E11D2E" }} connectNulls />
              <Area type="monotone" dataKey="readiness" name="readiness" stroke="#22C55E" strokeWidth={1.5}
                fill="transparent" strokeDasharray="4 4" dot={false} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-athlynx rounded" /> Career Velocity</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-success rounded" style={{ borderTop: "1px dashed" }} /> Career Readiness</span>
      </div>
    </CareerCard>
  );
}
